import { useCallback } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSLink,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { APP_CONFIG } from "@/domains/global/config";
import { windowModule } from "@/modules/window";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { IntegrationsOrganizingForChromeExtension } from "@/pages/integrations/chrome-extension/integrations-organizing-for-chrome-extension";
import { IntegrationsModelSelectionForChromeExtension } from "@/pages/integrations/chrome-extension/integrations-model-selection-for-chrome-extension";
import { IntegrationsLoader } from "@/pages/integrations/shared/integrations-loader";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
  SettingsIntegrationBenefitCard,
} from "@/pages/settings/components/settings-integration-layout";

export const SettingsChromeExtensionPage = observer(() => {
  const { store } = useAppStore();
  const { isLoading, load } = store.chromeExtension;

  store.chromeExtension.useIsChromeExtensionInstalledUpdater();

  useEffectOnMount(() => {
    load();
  });

  const isConnected = !!store.chromeExtension.isInstalled.data;

  const handlePress = useCallback(
    () => windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.CHROME_EXTENSION) }),
    []
  );

  const benefitPoints = [
    <>
      Install the <MDSLink onPress={handlePress}>Mem Chrome Extension</MDSLink> and sign in to your
      Mem account
    </>,
    "Pin it to your browser window to enable one-click web clipping",
    "Save entire webpages, or select specific text or images",
    "Mem will create and organize notes with the original webpages attached",
  ];

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Apps and integrations"
        title="Chrome extension"
        isConnected={!clientEnvModule.isDesktop() ? isConnected : undefined}
      />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <SettingsIntegrationBenefitCard points={benefitPoints} />
          {!isConnected && (
            <StyledDownloadButton onPress={handlePress}>
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.GoogleChrome} />
              Download Mem Chrome Extension
              <MDSIcon
                slot={MDSButtonSlotName.RightIcon}
                kind={MDSIconKind.ArrowUpRightFromSquare}
              />
            </StyledDownloadButton>
          )}
        </SettingsIntegrationSection>

        {isConnected && (
          <SettingsIntegrationSection>
            <StyledSectionHeading>
              <MDSText
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.SemiBold}
                color={MDSTextColor.Secondary}
              >
                CUSTOM INSTRUCTIONS
              </MDSText>
            </StyledSectionHeading>
            <StyledExplanationText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
              Mem automatically creates, updates, and organizes formatted notes based on the
              webpages you save
            </StyledExplanationText>
            {isLoading ? (
              <IntegrationsLoader />
            ) : (
              <StyledOrganizingOverride>
                <IntegrationsOrganizingForChromeExtension />
              </StyledOrganizingOverride>
            )}
            <StyledModelSelectionOverride>
              <IntegrationsModelSelectionForChromeExtension />
            </StyledModelSelectionOverride>
          </SettingsIntegrationSection>
        )}
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledDownloadButton = styled(MDSButton)`
  margin-top: ${({ theme }) => theme.spacing[3]};
  max-width: fit-content;
`;

const StyledSectionHeading = styled.div`
  padding-top: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  text-transform: uppercase;
  letter-spacing: 0.24px;
`;

/**
 * Hide the sub-component's own "Smart Handling" heading and explanation text
 * (first two direct children rendered by the IntegrationsOrganizingForChromeExtension
 * fragment) since we render our own "CUSTOM INSTRUCTIONS" heading above.
 */
const StyledOrganizingOverride = styled.div`
  width: 100%;

  > :first-child,
  > :nth-child(2) {
    display: none;
  }
`;

const StyledExplanationText = styled(MDSText)`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

/** Cap the sub-component's max-width to fit the modal content area. */
const StyledModelSelectionOverride = styled.div`
  width: 100%;
  max-width: 700px;
  padding-top: ${({ theme }) => theme.spacing[2]};
`;
